import { useTranslation } from 'react-i18next';
import { telHref } from '@d-orca/shared/client/utils';
import type { ProjectManager } from '@interfaces/IProject';
import { Tooltip, TooltipContent } from '@components/ui/Tooltip/Tooltip';
import styles from './styles.module.css';

type IManagerCardProps = { managers: ProjectManager[] };

/** How many managers the photo panel lists in full; the rest fold into "+N more". */
const SHOWN = 1;

/**
 * The project's project managers — its Trusstor management crew members
 * holding the project-manager trade. A project can have several: the first
 * is listed with their phone, the rest are named on hover.
 */
export function ManagerCard({ managers }: IManagerCardProps) {
  const { t } = useTranslation();
  const shown = managers.slice(0, SHOWN);
  const hidden = managers.slice(SHOWN);
  return (
    <div className={styles.card} data-testid="manager-card">
      <span className={styles.label}>{t('portfolio.card.managers.title', { count: Math.max(managers.length, 1) })}</span>
      {managers.length === 0 ? <div className={styles.name}>—</div> : null}
      {shown.map((manager, i) => (
        // Names aren't unique across a crew, so position joins the key.
        <div key={`${manager.name}#${i}`} className={styles.person}>
          <div className={styles.name}>{manager.name}</div>
          {manager.phone ? (
            <a className={styles.phone} href={telHref(manager.phone)} onClick={(e) => e.stopPropagation()}>
              {manager.phone}
            </a>
          ) : null}
        </div>
      ))}
      {hidden.length > 0 ? (
        <Tooltip content={<TooltipContent title={t('portfolio.card.managers.title', { count: managers.length })} lines={hidden.map((m) => m.name)} />}>
          <span className={styles.more} tabIndex={0}>
            {t('portfolio.card.managers.more', { count: hidden.length })}
          </span>
        </Tooltip>
      ) : null}
    </div>
  );
}
